import * as XLSX from 'xlsx';
import { FormData, TranslationFunction, AddressDetail, UboDetail, AttachmentItem, TaxablePersonDetails, JuridicalPersonSpecifics, NaturalPersonSpecifics, TaxGroupSpecifics, UnincorporatedPartnershipSpecifics, Elections, AccountingSchedule, AccountingAdjustments, ReliefApplications, OtherAdjustments, TaxLiability, Schedule13UaeDividendsData, Schedule14ForeignPEData, Schedule15TaxCreditData, Schedule16RelatedPartyData, Schedule16ConnectedPersonsData, Schedule17TaxLossData, Schedule17TaxGroupLossData, Schedule18ParticipationExemptionData, Schedule19InterestCappingData, Schedule20ReliefData, Schedule21TransitionalRulesData, Schedule22OtherSubScheduleData, FreeZoneScheduleData, BusinessActivityItem, RevenuePercentageItem, UnincorporatedPartnershipItem, TaxGroupMemberItem } from '../types';
import { translations } from '../i18n/translations';
import { calculateTaxableIncomeBeforeLossUtilisation, calculateTaxableIncomeAfterTaxLoss, calculateCTLiabilityBeforeCredits, calculateCorporateTaxPayable } from './calculations';


// --- HELPERS ---
const sanitizeSheetName = (name: string): string => {
  return name.replace(/[\\/?*[\]:]/g, '').substring(0, 31);
};

const isValueEmpty = (value: any): boolean => {
  if (value === null || value === undefined || value === '' || value === false || value === '0.00' || (typeof value === 'number' && value === 0)) return true;
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    return value.every(item => typeof item === 'object' && item !== null && Object.values(item).every(v => isValueEmpty(v) || Object.keys(item).length === 1 && item.hasOwnProperty('id')));
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    if (value instanceof File) return false;
    if (Object.keys(value).length === 0) return true;
    return Object.entries(value).every(([k, v]) => k === 'id' || isValueEmpty(v));
  }
  return false;
};


const hasData = (obj: any): boolean => {
    if (obj === null || obj === undefined) return false;
    if (typeof obj !== 'object') return !isValueEmpty(obj);
    if (Array.isArray(obj)) return obj.some(item => hasData(item));
    return Object.entries(obj).some(([key, value]) => {
      if (key.startsWith('total') && key.endsWith('Calculated')) return false;
      if (key === 'id') return false;
      return !isValueEmpty(value);
    });
};

const renderValue = (value: any, t: TranslationFunction, key?: string): any => {
    if (isValueEmpty(value)) return '';
    if (typeof value === 'boolean') return value ? t('yes') : t('no');
    if(key && (key.includes('emirate') || key.includes('Emirate'))) return t(`emirate_${String(value).toLowerCase()}` as any) || value;
    if(key && (key.includes('city') || key.includes('City'))) return t(`city_${String(value).toLowerCase()}_c` as any) || value;
    if(key && (key.includes('country') || key.includes('Country'))) return t(`country_${String(value).toLowerCase()}` as any) || value;
    if(key === 'filingAs') return t(`filing_as_${String(value)}` as any) || value;
    if(key === 'accountingBasis') return t(String(value) as 'cash' | 'accrual') || value;
    if(key === 'nonResidentFilingBasis' && value) return t(value as any) || value;
    return String(value);
};

// --- PDF/PRINT GENERATION ---

export const printAsPdf = (formData: FormData, t: TranslationFunction): void => {
    const originalTitle = document.title;
    document.title = t('pdf_export_title') + (formData.taxablePersonDetails.trn ? ` - TRN ${formData.taxablePersonDetails.trn}` : '');
    
    window.print();
    
    document.title = originalTitle;
};


// --- EXCEL EXPORT ---

export const exportToExcel = (formData: FormData, t: TranslationFunction): void => {
    const wb = XLSX.utils.book_new();

    const buildAndAddSheet = (sheetName: string, buildFn: () => (string | number)[][]) => {
        const sheetData = buildFn();
        if (sheetData.length > 2) { 
            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            const title = sheetData[0][0];
            ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }]; 
            ws['!cols'] = [{ wch: 45 }, { wch: 35 }, { wch: 35 }, { wch: 35 }]; 
            XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(sheetName));
        }
    };

    const buildStep1Sheet = () => {
        const data: any[][] = [[t('s1_title_full')], [], [t('excel_header_label'), t('excel_header_value')]];
        const details = formData.taxablePersonDetails;

        const addRow = (labelKey: keyof typeof translations.en, value: any, renderKey?: string) => {
            if (!isValueEmpty(value)) {
                data.push([t(labelKey), renderValue(value, t, renderKey || labelKey)]);
            }
        };

        addRow('filing_as_label', details.filingAs, 'filingAs');
        addRow('taxable_person_name_label', details.taxablePersonName);
        addRow('trn_label', details.trn);
        addRow('primary_business_activity_label', details.primaryBusinessActivity);
        addRow('tax_period_start_date_label', details.taxPeriodStartDate);
        addRow('tax_period_end_date_label', details.taxPeriodEndDate);

        if (hasData(details.mainAddress)) {
            data.push([]); data.push([t('main_business_address_title')]);
            addRow('address_line1_label', details.mainAddress.addressLine1);
            addRow('address_line2_label', details.mainAddress.addressLine2);
            addRow('city_label', details.mainAddress.city, 'city');
            addRow('emirate_state_label', details.mainAddress.emirateOrState, 'emirate');
            addRow('po_box_label', details.mainAddress.poBox);
            addRow('country_label', details.mainAddress.country, 'country');
        }

        if (hasData(details.contactPerson)) {
            data.push([]); data.push([t('contact_person_details_title')]);
            addRow('contact_name_label', details.contactPerson.contactName);
            addRow('contact_email_label', details.contactPerson.contactEmail);
            addRow('contact_mobile_label', details.contactPerson.contactMobile);
            addRow('contact_landline_label', details.contactPerson.contactLandline);
        }

        if (details.isUboInfoDisclosed === 'yes' && hasData(details.uboDetails)) {
            data.push([]); data.push([t('ubo_information_title')]);
            data.push([t('ubo_name_label'), t('ubo_trn_tax_id_label'), t('ubo_nationality_label'), t('ubo_country_of_residence_label')]);
            details.uboDetails.filter(hasData).forEach(ubo => {
                data.push([ubo.uboName, ubo.uboTrnTaxId, renderValue(ubo.uboNationality, t, 'country'), renderValue(ubo.uboCountryOfResidence, t, 'country')]);
            });
        }
        
        const specificsKey = (Object.keys(details) as Array<keyof TaxablePersonDetails>).find(k => k.endsWith('Specifics'));
        if(specificsKey && hasData(details[specificsKey])) {
            const specifics = details[specificsKey] as any;
            data.push([]); data.push([t(`${specificsKey}_title` as any)]);
             Object.entries(specifics).forEach(([key, value]) => {
                if(!isValueEmpty(value) && typeof value !== 'object' && key !== 'id') {
                    addRow(`${key}_label` as any, value, key);
                }
             });
             if(hasData(specifics.businessActivityList)) {
                data.push([]); data.push([t('business_activity_list_label')]);
                 specifics.businessActivityList.forEach((item: BusinessActivityItem, i:number) => data.push([`${t('activity_label')} ${i+1}`, item.activity]));
             }
             if(hasData(specifics.revenuePercentageBySector)) {
                data.push([]); data.push([t('revenue_percentage_by_sector_label')]);
                 specifics.revenuePercentageBySector.forEach((item: RevenuePercentageItem, i:number) => data.push([`${t('sector_label')} ${i+1}`, `${item.sector}: ${item.percentage}%`]));
             }
             if(hasData(specifics.unincorporatedPartnerships)) {
                data.push([]); data.push([t('unincorporated_partnership_details_label')]);
                 specifics.unincorporatedPartnerships.forEach((item: UnincorporatedPartnershipItem, i:number) => data.push([`${item.name}`, `TRN: ${item.trn}`]));
             }
        }

        return data;
    };
    buildAndAddSheet(t('s1_sheet_name'), buildStep1Sheet);

    const buildStep2Sheet = () => {
      const data: any[][] = [[t('s2_title_full')], [], [t('excel_header_label'), t('excel_header_value')]];
      Object.entries(formData.elections).forEach(([key, value]) => {
          if(!isValueEmpty(value)){
              data.push([t(`${key}_label` as any), renderValue(value, t)]);
          }
      });
      return data;
    }
    buildAndAddSheet(t('s2_sheet_name'), buildStep2Sheet);

    if (formData.elections.smallBusinessRelief) {
        buildAndAddSheet('SBR', () => [[t('sbr_active_title')], [], [t('sbr_active_message')]]);
    } else {
        const stepsData: { sheetNameKey: keyof typeof translations.en, titleKey: keyof typeof translations.en, data: any}[] = [
            { sheetNameKey: 's3_sheet_name', titleKey: 's3_title_full', data: formData.accounting },
            { sheetNameKey: 's4_sheet_name', titleKey: 's4_title_full', data: formData.accountingAdjustments },
            { sheetNameKey: 's5_sheet_name', titleKey: 's5_title_full', data: formData.relief },
            { sheetNameKey: 's6_sheet_name', titleKey: 's6_title_full', data: formData.otherAdjustments }
        ];

        stepsData.forEach(step => {
            if (hasData(step.data)) {
                buildAndAddSheet(t(step.sheetNameKey), () => {
                    const sheetData: any[][] = [[t(step.titleKey)], [], [t('excel_header_label'), t('excel_header_value')]];
                     Object.entries(step.data).forEach(([key, value]) => {
                        if(!isValueEmpty(value)) {
                            sheetData.push([t(`${key}_label` as any) || key, renderValue(value, t)]);
                        }
                    });
                    return sheetData;
                });
            }
        });

        const calculatedLiabilityData = {
            taxableIncomeLossBeforeTaxLossUtilisation: calculateTaxableIncomeBeforeLossUtilisation(formData).toFixed(2), 
            ...formData.taxLiability,
            taxableIncomeAfterTaxLossAdjustments: calculateTaxableIncomeAfterTaxLoss(calculateTaxableIncomeBeforeLossUtilisation(formData), formData.taxLiability.taxLossesUtilisedCurrentPeriod).toFixed(2),
            corporateTaxLiabilityBeforeCredits: calculateCTLiabilityBeforeCredits(calculateTaxableIncomeAfterTaxLoss(calculateTaxableIncomeBeforeLossUtilisation(formData), formData.taxLiability.taxLossesUtilisedCurrentPeriod), formData.elections, formData.taxablePersonDetails, formData.freeZoneSchedule).toFixed(2),
            corporateTaxPayable: calculateCorporateTaxPayable(calculateCTLiabilityBeforeCredits(calculateTaxableIncomeAfterTaxLoss(calculateTaxableIncomeBeforeLossUtilisation(formData), formData.taxLiability.taxLossesUtilisedCurrentPeriod), formData.elections, formData.taxablePersonDetails, formData.freeZoneSchedule), formData.taxLiability.taxCreditsApplied).toFixed(2),
        };

        if(hasData(calculatedLiabilityData)) {
            buildAndAddSheet(t('s7_sheet_name'), () => {
                const sheetData: any[][] = [[t('s7_title_full')], [], [t('excel_header_label'), t('excel_header_value')]];
                Object.entries(calculatedLiabilityData).forEach(([key, value]) => {
                     if(!isValueEmpty(value)) {
                         sheetData.push([t(`${key}_label` as any), renderValue(value, t)]);
                     }
                });
                return sheetData;
            });
        }
        
    }

    const buildDeclarationSheet = () => {
      const data: any[][] = [[t('declaration_title')], [], [t('excel_header_label'), t('excel_header_value')]];
      Object.entries(formData.review).forEach(([key, value]) => {
        if(!isValueEmpty(value)) {
            data.push([t(`${key}_label` as any), renderValue(value, t)]);
        }
      });
      return data;
    }
    buildAndAddSheet(t('step8_title'), buildDeclarationSheet);

    XLSX.writeFile(wb, "GoldenGavel_TaxReturn_Export.xlsx");
};